import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Loader, Pill } from "@/components/Bits";

export default function TopicTest() {
  const { topicId } = useParams();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const start = useCallback(async () => {
    setTest(null);
    setResult(null);
    setAnswers({});
    try {
      const { data } = await api.post(`/topics/${topicId}/test`);
      setTest(data);
    } catch (err) {
      toast.error(errMsg(err));
      navigate(`/topic/${topicId}`);
    }
  }, [topicId, navigate]);

  useEffect(() => {
    start();
  }, [start]);

  const generating = test?.status === "generating";

  useEffect(() => {
    if (!generating) return;
    const id = setInterval(async () => {
      const { data } = await api.post(`/topics/${topicId}/test`);
      setTest(data);
    }, 6000);
    return () => clearInterval(id);
  }, [generating, topicId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tests/submit", { test_id: test.test_id, answers });
      setResult(data);
      data.passed ? toast.success(`Passed with ${data.score}% — next topic unlocked`) : toast.error(`Scored ${data.score}% — needs ${data.pass_threshold}%`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!test || generating) return <Shell><Loader label="Assessment agent is writing your test…" note="Basic and intermediate questions covering the whole topic." /></Shell>;
  if (busy) return <Shell><Loader label="Grading…" note="Objective answers auto-graded; open answers graded by AI with a confidence flag." /></Shell>;

  if (result)
    return (
      <Shell>
        <div className="max-w-3xl reveal" data-testid="test-result">
          <p className="rule-label">End-of-topic test · {test.title}</p>
          <div className="mt-5 flex items-center gap-4">
            {result.passed ? <CheckCircle2 className="w-10 h-10 text-emerald-600" /> : <XCircle className="w-10 h-10 text-rose-600" />}
            <div>
              <h1 className="text-4xl font-extrabold" data-testid="test-score">{result.score}%</h1>
              <p className="text-sm text-slate-500">
                {result.passed ? "Topic mastered — the next topic in this subject is unlocked." : `You need ${result.pass_threshold}% to unlock the next topic.`}
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            {result.results.map((r, i) => (
              <div key={r.question_id} className="card-flat p-6" data-testid={`test-review-${i}`}>
                <div className="flex items-center gap-2">
                  <Pill tone={r.correct ? "green" : "red"}>{r.correct ? "correct" : "review"}</Pill>
                  <Pill tone="slate">{r.awarded} pts</Pill>
                  {r.graded_by === "ai" && <Pill tone="amber">AI graded · {r.confidence} confidence</Pill>}
                </div>
                <p className="mt-3 font-semibold text-sm">{r.question}</p>
                <p className="mt-2 text-sm text-slate-600">Your answer: <span className="font-medium">{r.your_answer || "—"}</span></p>
                <p className="text-sm text-slate-600">Expected: <span className="font-medium">{r.correct_answer}</span></p>
                {r.explanation && <p className="mt-2 text-sm text-slate-500 leading-relaxed">{r.explanation}</p>}
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {!result.passed && (
              <button data-testid="test-retry" onClick={start} className="px-6 py-3 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
                Try a fresh test
              </button>
            )}
            <button data-testid="test-back-dashboard" onClick={() => navigate("/dashboard")} className="px-6 py-3 rounded-full border border-slate-300 text-sm font-semibold hover:border-slate-500 transition-colors">
              Back to today
            </button>
          </div>
        </div>
      </Shell>
    );

  return (
    <Shell>
      <div className="max-w-3xl reveal" data-testid="test-root">
        <p className="rule-label">End-of-topic test · pass mark {test.pass_threshold}%</p>
        <h1 className="mt-4 text-3xl font-extrabold">{test.title}</h1>
        <p className="mt-2 text-sm text-slate-500">Basic and intermediate questions covering the whole topic.</p>

        <div className="mt-8 space-y-4">
          {test.questions.map((q, i) => (
            <div key={q.question_id} className="card-flat p-6" data-testid={`test-question-${i}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono-x text-xs text-slate-400">Q{i + 1}</span>
                <Pill tone="slate">{q.level}</Pill>
                <Pill tone="blue">{Math.round(q.points)} pts</Pill>
              </div>
              <p className="mt-3 font-semibold">{q.question}</p>
              {q.type === "mcq" ? (
                <div className="mt-4 space-y-2">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`test-option-${i}-${q.options.indexOf(opt)}`}
                      onClick={() => setAnswers({ ...answers, [q.question_id]: opt })}
                      className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                        answers[q.question_id] === opt ? "border-blue-600 bg-blue-50 font-medium" : "border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  data-testid={`test-answer-${i}`}
                  rows={q.type === "open" ? 5 : 2}
                  value={answers[q.question_id] || ""}
                  onChange={(e) => setAnswers({ ...answers, [q.question_id]: e.target.value })}
                  className="mt-4 w-full px-4 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-blue-500"
                  placeholder={q.type === "open" ? "Explain in your own words…" : "Your answer"}
                />
              )}
            </div>
          ))}
        </div>

        <button data-testid="test-submit" onClick={submit} className="mt-8 px-7 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
          Submit test
        </button>
      </div>
    </Shell>
  );
}
